import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { INVITATION_STATUSES } from "@/shared/invitations/constants";
import { InvitationsResults } from "@/shared/invitations/InvitationsResults";
import type { AdminInvitationsList, InvitationStatus } from "@/shared/invitations/types";

interface OrganizationInvitationsSectionProps {
  readonly status: InvitationStatus | null;
  readonly onStatusChange: (status: InvitationStatus | null) => void;
  readonly page: number;
  readonly onPageChange: (page: number) => void;
  readonly data: AdminInvitationsList | undefined;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
}

const SELECT_CLASS =
  "flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring";

/**
 * Invitations of one organization (status filter + pager). Invitee emails are
 * PII: rendered, never logged and never put in the URL; the list read is
 * audited and throttled by the API, so errors never echo a server message.
 */
export function OrganizationInvitationsSection({
  status,
  onStatusChange,
  page,
  onPageChange,
  data,
  isLoading,
  isFetching,
  isError,
  error,
  onRetry,
}: OrganizationInvitationsSectionProps) {
  const { t } = useTranslation();
  const emptyBody = t(
    status === "PENDING"
      ? "organizations.invitations.empty.pending"
      : status === null
        ? "organizations.invitations.empty.none"
        : "organizations.invitations.empty.all",
  );

  return (
    <section
      data-testid="organization-invitations"
      className="space-y-4 rounded-lg border border-border bg-card p-6"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="brand-eyebrow">{t("organizations.invitations.title")}</h2>
          <p
            data-testid="organization-pii-note"
            className="flex items-center gap-2 font-sans text-sm text-muted-foreground"
          >
            <ShieldAlert className="h-4 w-4" aria-hidden="true" />
            {t("invitations.piiNote")}
          </p>
        </div>
        <div className="w-full space-y-1.5 sm:w-auto">
          <label
            htmlFor="organization-invitations-status"
            className="font-sans text-sm font-medium text-foreground"
          >
            {t("invitations.filters.status")}
          </label>
          <select
            id="organization-invitations-status"
            data-testid="organization-invitations-status"
            value={status ?? ""}
            onChange={(e) =>
              onStatusChange(e.target.value === "" ? null : (e.target.value as InvitationStatus))
            }
            className={SELECT_CLASS}
          >
            <option value="">{t("invitations.filters.statusAll")}</option>
            {INVITATION_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`invitations.status.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <InvitationsResults
        testIdPrefix="organization-invitations"
        data={data}
        page={page}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        error={error}
        onRetry={onRetry}
        onPageChange={onPageChange}
        emptyBody={emptyBody}
        pastEndBody={t("organizations.invitations.empty.pastEnd")}
        hideOrganization
      />
    </section>
  );
}
